/**
 * Picks a PERSON off the staff directory - one of them, or several.
 *
 * `ClientPicker` and `PartyPicker` both search a table over the wire; this one
 * deliberately does not. The directory is tens of rows and is already in memory
 * behind `useStaff`, so filtering it here is not an optimisation but the correct
 * shape: a debounced round trip per keystroke would be slower than a substring
 * test on twenty names, and it would stop answering the moment the connection
 * did, on a list the browser is already holding.
 *
 * ONE component for both modes rather than a single and a multi export. They
 * differ in two lines - what `value` holds and what `onChange` is handed - while
 * everything under that (the box, the filter, the panel, the chips, the closing
 * behaviour) is the same markup, and two copies of it would drift the first time
 * one of them grew a shortcut. The union on the props keeps the caller honest:
 * `multiple` decides whether `value` is a list or a single id, and TypeScript
 * refuses the pairing that does not match.
 *
 * Deactivated people stay in the list, muted and last (`useStaff` sorts them
 * there). They have to remain pickable - an old time entry still needs its
 * author corrected - but must not read as a candidate for new work.
 *
 * Drawn as `ClientPicker`'s field-plus-panel rather than through `Pop`: `Pop`
 * renders into the body and reads its open slot from a `PopHost`, which is
 * mounted per section here, not around the app. A shared primitive that six
 * screens will drop into forms and toolbars cannot assume one above it.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Check, Loader2, Search, X } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { Avatar } from "../../ui";
import { staffLabel, useStaff } from "./staff";

interface CommonProps {
  placeholder?: string;
  disabled?: boolean;
}

interface SingleProps extends CommonProps {
  multiple?: false;
  value: string | null;
  onChange: (value: string | null) => void;
}

interface ManyProps extends CommonProps {
  multiple: true;
  value: string[];
  onChange: (value: string[]) => void;
}

export type UserPickerProps = SingleProps | ManyProps;

export function UserPicker(props: UserPickerProps) {
  const t = useT();
  const { staff, loading } = useStaff();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const disabled = props.disabled ?? false;

  // Both modes reduce to a list here; only `toggle` and `remove` below know
  // which shape has to go back out.
  const selected = props.multiple ? props.value : props.value ? [props.value] : [];

  // A click outside closes the panel, the same way `ClientPicker` does it.
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const matches = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return staff;
    // Email as well as name: two people called Mari are told apart by nothing
    // else, and an operator who knows the address types that instead.
    return staff.filter(
      (s) => s.name.toLowerCase().includes(needle) || s.email.toLowerCase().includes(needle),
    );
  }, [q, staff]);

  function toggle(id: string) {
    const off = selected.includes(id);
    if (props.multiple) {
      props.onChange(off ? selected.filter((x) => x !== id) : [...selected, id]);
      // The query stays: picking two people out of one search is the common
      // case, and clearing the box would make the operator type it again.
      return;
    }
    // Single mode: one click is the whole interaction. Clicking the name that is
    // already in the box takes it back out, which is the only way to empty a
    // field that has no separate clear control.
    props.onChange(off ? null : id);
    setQ("");
    setOpen(false);
  }

  function remove(id: string) {
    if (props.multiple) props.onChange(selected.filter((x) => x !== id));
    else props.onChange(null);
  }

  return (
    <div ref={boxRef} className="relative">
      <label className={cn("crm-inp", disabled && "opacity-60")}>
        <input
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => e.key === "Escape" && setOpen(false)}
          placeholder={props.placeholder ?? t("common.search")}
          disabled={disabled}
          autoComplete="off"
        />
        {loading ? (
          <Loader2 className="size-4 shrink-0 animate-spin text-muted-foreground" />
        ) : (
          <Search className="size-4 shrink-0 text-muted-foreground" />
        )}
      </label>

      {open && !disabled && matches.length > 0 ? (
        <ul className="absolute z-50 mt-2 max-h-72 w-full overflow-auto rounded-3xl bg-[var(--crm-surface)] p-1.5 shadow-[var(--crm-sh-lg)]">
          {matches.map((s) => {
            const on = selected.includes(s.id);
            return (
              <li key={s.id}>
                <button
                  type="button"
                  // Keep focus on the field until the click registers.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => toggle(s.id)}
                  className={cn(
                    "flex w-full items-center gap-2.5 rounded-2xl px-3 py-2 text-left hover:bg-[var(--crm-sunk)]",
                    !s.active && "opacity-55",
                  )}
                >
                  <Avatar name={s.name} src={s.avatarUrl} size={28} />
                  <span className="min-w-0 flex-1">
                    <span className="crm-prow-n crm-trunc block">{s.name}</span>
                    <span className="crm-trunc block text-[11.5px] text-muted-foreground">{s.email}</span>
                  </span>
                  {!s.active ? (
                    <span className="shrink-0 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {t("common.inactive")}
                    </span>
                  ) : null}
                  {on ? <Check className="size-4 shrink-0 text-[var(--color-primary)]" /> : null}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}

      {selected.length ? (
        <span className="mt-2 flex flex-wrap gap-1.5">
          {selected.map((id) => (
            <PickedChip key={id} id={id} disabled={disabled} onRemove={() => remove(id)} />
          ))}
        </span>
      ) : null}
    </div>
  );
}

/**
 * One picked person, with the way back off the list.
 *
 * Resolved through the directory rather than through the row that was clicked:
 * the value can arrive from the record being edited, whose ids were never in
 * this component's hands, and an id that resolves to nobody still has to draw
 * something (see `staffLabel`).
 */
function PickedChip({ id, disabled, onRemove }: { id: string; disabled: boolean; onRemove: () => void }) {
  const t = useT();
  const { byId } = useStaff();
  const person = byId.get(id);
  const label = staffLabel(byId, id);

  return (
    <span className={cn("crm-chip", person && !person.active && "opacity-60")}>
      <Avatar name={label} src={person?.avatarUrl} size={16} />
      {label}
      {!disabled ? (
        <button
          type="button"
          className="-mr-1.5 inline-flex rounded-full p-0.5 opacity-70 hover:opacity-100"
          onClick={onRemove}
          aria-label={`${t("common.remove")}: ${label}`}
        >
          <X className="size-3" />
        </button>
      ) : null}
    </span>
  );
}
